import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline'

function RecoveryModal({
  isOpen,
  onClose,
  onRecover,
  account,
  deletedAt
}) {
  const { t } = useTranslation('settings')
  const [isRecovering, setIsRecovering] = useState(false)
  const [recovered, setRecovered] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState('')

  // Calculate time remaining for recovery
  useEffect(() => {
    if (!isOpen || !deletedAt) return

    const updateTimer = () => {
      const deletionTime = new Date(deletedAt)
      const expirationTime = new Date(deletionTime.getTime() + 24 * 60 * 60 * 1000) // 24 hours later
      const now = new Date()
      const remaining = expirationTime - now

      if (remaining <= 0) {
        setTimeRemaining(t('accounts.recovery.modal.expired'))
        return
      }

      const hours = Math.floor(remaining / (1000 * 60 * 60))
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000)

      if (hours > 0) {
        setTimeRemaining(t('accounts.recovery.modal.timeRemaining.hours', { hours, minutes }))
      } else if (minutes > 0) {
        setTimeRemaining(t('accounts.recovery.modal.timeRemaining.minutes', { minutes, seconds }))
      } else {
        setTimeRemaining(t('accounts.recovery.modal.timeRemaining.seconds', { seconds }))
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }, [isOpen, deletedAt, t])

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isRecovering) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, isRecovering, onClose])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRecovered(false)
      setIsRecovering(false)
    }
  }, [isOpen])

  if (!isOpen || !account) return null

  const handleRecover = async () => {
    setIsRecovering(true)
    try {
      await onRecover(account.id)
      setRecovered(true)
      // Close modal after showing success
      setTimeout(() => onClose(), 2000)
    } catch (error) {
      console.error('Failed to recover account:', error)
      setIsRecovering(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={!isRecovering ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200"
        role="dialog"
        aria-labelledby="recovery-modal-title"
        aria-describedby="recovery-modal-description"
      >
        <div className="p-6">
          {!recovered ? (
            <>
              {/* Icon */}
              <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-green-100 mb-4">
                <ClockIcon className="h-7 w-7 text-green-600" aria-hidden="true" />
              </div>

              {/* Title */}
              <h3
                id="recovery-modal-title"
                className="text-lg font-semibold text-gray-900 text-center mb-2"
              >
                {t('accounts.recovery.modal.title')}
              </h3>

              {/* Account info */}
              <div className="flex items-center justify-center space-x-3 mb-4">
                {account.profile_picture_url && (
                  <img
                    src={account.profile_picture_url}
                    alt={account.username}
                    className="w-10 h-10 rounded-full"
                  />
                )}
                <span className="font-medium text-gray-900">@{account.username}</span>
              </div>

              {/* Description */}
              <div id="recovery-modal-description" className="space-y-3">
                <p className="text-sm text-gray-600 text-center">
                  {t('accounts.recovery.modal.description')}
                </p>

                {/* Time remaining */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center justify-center space-x-2">
                    <ClockIcon className="h-5 w-5 text-amber-600" />
                    <div className="text-center">
                      <p className="text-sm font-medium text-amber-900">
                        {t('accounts.recovery.modal.countdown.title')}
                      </p>
                      <p className="text-lg font-bold text-amber-800 mt-1">
                        {timeRemaining}
                      </p>
                    </div>
                  </div>
                </div>

                {/* What happens */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-900 mb-2">
                    {t('accounts.recovery.modal.whatHappens.title')}
                  </p>
                  <ul className="space-y-1 text-sm text-green-800">
                    <li className="flex items-start">
                      <span className="mr-2">✓</span>
                      <span>{t('accounts.recovery.modal.whatHappens.item1')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">✓</span>
                      <span>{t('accounts.recovery.modal.whatHappens.item2')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">✓</span>
                      <span>{t('accounts.recovery.modal.whatHappens.item3')}</span>
                    </li>
                  </ul>
                </div>

                <p className="text-xs text-gray-500 text-center">
                  {t('accounts.recovery.modal.note')}
                </p>
              </div>

              {/* Actions */}
              <div className="mt-6 flex items-center justify-end space-x-3">
                <button
                  onClick={onClose}
                  disabled={isRecovering}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('accounts.recovery.modal.button.cancel')}
                </button>
                <button
                  onClick={handleRecover}
                  disabled={isRecovering || timeRemaining === t('accounts.recovery.modal.expired')}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center space-x-2"
                >
                  {isRecovering && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  <span>
                    {isRecovering
                      ? t('accounts.recovery.modal.button.recovering')
                      : t('accounts.recovery.modal.button.recover')
                    }
                  </span>
                </button>
              </div>
            </>
          ) : (
            /* Success state */
            <>
              <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-green-100 mb-4">
                <CheckCircleIcon className="h-7 w-7 text-green-600" />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                {t('accounts.recovery.modal.success.title')}
              </h3>

              <p className="text-sm text-gray-600 text-center">
                {t('accounts.recovery.modal.success.message')}
              </p>

              <div className="mt-6 flex justify-center">
                <button
                  onClick={onClose}
                  className="px-6 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  {t('accounts.recovery.modal.success.button')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

RecoveryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRecover: PropTypes.func.isRequired,
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
    profile_picture_url: PropTypes.string
  }),
  deletedAt: PropTypes.string
}

export default RecoveryModal